// AssistantEspaceTravail — « Créer un espace de travail », 6 étapes.
//
// 🔑 CET ASSISTANT N'IMPLÉMENTE RIEN. Il collecte, il montre, il APPELLE
//    `POST /api/platform/workspaces` — qui appelle lui-même
//    `creer_espace_de_travail()`, la SEULE implémentation, partagée avec le CLI
//    `scripts.maintenance.onboarding_tenant`. ⛔ Deux implémentations de la même
//    logique divergent toujours (c'est ce qui a produit la double autorité B2).
//
// 🔴 DEC-079 — UNKNOWN ≠ ENABLED. L'étape 3 montre les TROIS états :
//    activées · désactivées · primitives Platform NON gouvernées. Une capacité
//    métier non explicitement accordée reste False — ⛔ elle n'est pas « absente ».
import { useState, useEffect, useMemo, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Check, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { OhField, OhInput, OhSelect, OhStatusNote } from "../../../components/ui/oh-charte";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

// Primitives PLATFORM — ⛔ pas d'interrupteur tenant, par décision.
// Les afficher est un acte d'honnêteté : l'exploitant doit savoir ce qui est
// fourni sans être gouvernable, plutôt que de les chercher en vain.
const PRIMITIVES_PLATFORM = [
  { id: "pdf", nom: "Génération PDF", motif: "171 fichiers producteurs, toutes verticales — l'éteindre casserait le socle" },
  { id: "identite", nom: "Identité & tenants", motif: "socle de la plateforme" },
  { id: "audit", nom: "Journal & audit", motif: "socle de gouvernance" },
];

const ETAPES = ["Identité", "Hubs & modules", "Capacités", "Utilisateur", "Séquences", "Récapitulatif"];
const SEQ_PROPOSEES = ["CLIENT", "DOSSIER", "DEVIS", "FACTURE"];

const slugifier = (s) =>
  (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 32);

const libelleDe0 = (liste, id) => liste.find((x) => x.id === id)?.libelle || id;

export default function AssistantEspaceTravail({ onFerme, onCree }) {
  const [etape, setEtape] = useState(0);
  const [catalogue, setCatalogue] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);
  // Listes fermées servies par le serveur (métiers, parcours, activités) — jamais recopiées ici.
  const [listes, setListes] = useState({ metiers: [], parcours: [], activites: [] });
  const [listesErreur, setListesErreur] = useState(false);

  const [f, setF] = useState({
    nom: "", slug: "", slugTouche: false, societe: "", business_model: "",
    dossier_creation_profile: "", dossier_activite: "",
    // Hubs, modules, capacités et séquences viennent du PRÉRÉGLAGE du métier (serveur) dès qu'un métier est choisi.
    hubs_actifs: [], modules: [], capabilities: [], sequences: [],
    admin_nom: "", admin_email: "",
  });
  const maj = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const [rc, rh, rm] = await Promise.all([
          fetch(`${API}/api/platform/features/available`, { headers: auth(), credentials: "include" }),
          fetch(`${API}/api/platform/hubs-catalog`, { headers: auth(), credentials: "include" }),
          fetch(`${API}/api/platform/metiers`, { headers: auth(), credentials: "include" }),
        ]);
        if (rc.ok) { const d = await rc.json(); setCatalogue(Array.isArray(d) ? d : d.features || []); }
        if (rh.ok) { const d = await rh.json(); setHubs(d.hubs || []); }
        if (rm.ok) { setListes(await rm.json()); } else { setListesErreur(true); }
      } catch { setListesErreur(true); /* l'assistant reste ouvert : les listes se vident, pas l'écran */ }
    })();
  }, []);

  const slugFinal = f.slugTouche ? f.slug : slugifier(f.nom);
  const bascule = (liste, v) => (liste.includes(v) ? liste.filter((x) => x !== v) : [...liste, v]);

  const parCategorie = useMemo(() => {
    const g = {};
    for (const c of catalogue) {
      const hub = c.parent_hub || "autres";
      (g[hub] = g[hub] || []).push(c);
    }
    return g;
  }, [catalogue]);

  const valide = useMemo(() => {
    if (etape === 0) {
      return f.nom.trim().length > 1 && /^[a-z0-9_]{2,32}$/.test(slugFinal)
        && f.business_model !== "" && f.dossier_creation_profile !== "" && f.dossier_activite !== "";
    }
    if (etape === 3) return /\S+@\S+\.\S+/.test(f.admin_email);
    return true;
  }, [etape, f, slugFinal]);

  const creer = useCallback(async () => {
    setEnCours(true); setErreur(null);
    try {
      const res = await fetch(`${API}/api/platform/workspaces`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        credentials: "include",
        body: JSON.stringify({
          nom: f.nom.trim(), slug: slugFinal, admin_email: f.admin_email.trim(),
          admin_nom: f.admin_nom || null, societe: f.societe || null,
          business_model: f.business_model || null,
          dossier_creation_profile: f.dossier_creation_profile,
          dossier_activite: f.dossier_activite,
          hubs_actifs: f.hubs_actifs, modules: f.modules,
          capabilities: f.capabilities, sequences: f.sequences,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.detail || `HTTP ${res.status}`);
      toast.success(`Espace « ${d.slug} » créé — ${d.capabilities_ouvertes?.length} capacités ouvertes`);
      onCree?.(d);
    } catch (e) {
      setErreur(e.message || "Création impossible");
    } finally {
      setEnCours(false);
    }
  }, [f, slugFinal, onCree]);

  // Le métier propose son préréglage complet (parcours, activité, hubs, modules, capacités, séquences) ;
  // l'exploitant peut tout modifier ensuite. Changer de métier REMPLACE les propositions précédentes.
  const choisirMetier = (id) => {
    const m = listes.metiers.find((x) => x.id === id);
    setF((p) => ({
      ...p, business_model: id,
      dossier_creation_profile: m?.parcours_defaut || "",
      dossier_activite: m?.activite_defaut || "",
      hubs_actifs: [...(m?.hubs_actifs || [])],
      modules: (m?.modules || []).map((x) => [...x]),
      capabilities: [...(m?.capabilities || [])],
      sequences: [...(m?.sequences || [])],
    }));
  };
  const metierChoisi = libelleDe0(listes.metiers, f.business_model);
  const encartPrereglage = (
    <div className="rounded-lg p-3 mb-3 text-sm" data-testid="assistant-prereglage"
         style={{ background: "var(--oh-info-bg)", color: "var(--oh-info-text)", border: "0.5px solid var(--oh-info-border)" }}>
      Proposé par le métier « {metierChoisi} ». Tout reste modifiable.
    </div>
  );

  const champ = (label, k, opts = {}) => (
    <OhField label={label} htmlFor={`assistant-${k}`} className="mb-3">
      <OhInput
        id={`assistant-${k}`}
        value={f[k]} onChange={(e) => maj(k, e.target.value)}
        placeholder={opts.placeholder || ""}
        data-testid={`assistant-${k}`}
      />
    </OhField>
  );

  const puce = (actif, texte, onClick, testid) => (
    <button key={texte} type="button" onClick={onClick} data-testid={testid}
      className="px-3 py-1.5 rounded-lg text-sm text-left"
      style={{ background: actif ? "var(--oh-stat-green-bg)" : "var(--oh-bg-surface)",
               color: actif ? "var(--oh-stat-green-text)" : "var(--oh-text-secondary)",
               border: `0.5px solid ${actif ? "var(--oh-stat-green-border)" : "var(--oh-border)"}` }}>
      {actif ? "✓ " : "○ "}{texte}
    </button>
  );

  const refusees = catalogue.length - f.capabilities.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-3xl rounded-xl flex flex-col"
           style={{ background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
                    maxHeight: "88vh" }}
           data-testid="assistant-espace-travail">
        {/* En-tête */}
        <div className="flex items-center justify-between p-4"
             style={{ borderBottom: "0.5px solid var(--oh-border)" }}>
          <div>
            <h2 className="text-base" style={{ color: "var(--oh-text)", fontWeight: 500 }}>
              Créer un espace de travail
            </h2>
            <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
              Étape {etape + 1} sur {ETAPES.length} — {ETAPES[etape]}
            </p>
          </div>
          <button type="button" onClick={onFerme} aria-label="Fermer l'assistant"
                  data-testid="assistant-fermer"
                  style={{ color: "var(--oh-text-secondary)" }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Fil des étapes */}
        <div className="flex gap-1 px-4 pt-3">
          {ETAPES.map((e, i) => (
            <div key={e} className="flex-1 h-1 rounded"
                 style={{ background: i <= etape ? "var(--oh-accent-solid)" : "var(--oh-border)" }} />
          ))}
        </div>

        {/* Corps */}
        <div className="p-4 overflow-y-auto" style={{ flex: 1 }}>
          {etape === 0 && (
            <>
              {champ("Nom de l'organisation", "nom", { placeholder: "Ex : Mario Bat" })}
              <OhField label="Identifiant (slug)" htmlFor="assistant-slug" className="mb-3"
                       hint="C'est lui qui fait autorité dans toute la plateforme."
                       error={!/^[a-z0-9_]{2,32}$/.test(slugFinal)
                         ? "2 à 32 caractères, minuscules, chiffres et « _ » uniquement." : undefined}>
                <OhInput
                  id="assistant-slug" data-testid="assistant-slug" value={slugFinal}
                  invalid={!/^[a-z0-9_]{2,32}$/.test(slugFinal)}
                  onChange={(e) => { maj("slugTouche", true); maj("slug", slugifier(e.target.value)); }}
                  style={{ fontFamily: "monospace" }}
                />
              </OhField>
              {champ("Société (raison sociale)", "societe", { placeholder: "Optionnel" })}
              {listesErreur && (
                <OhStatusNote variant="error" className="mb-3" data-testid="assistant-listes-erreur">
                  Les listes de métiers n'ont pas pu être chargées. Fermez l'assistant et rouvrez-le.
                </OhStatusNote>
              )}
              <OhField label="Métier" htmlFor="assistant-business_model" required className="mb-3"
                       hint="Le métier propose un parcours de dossier, modifiable ci-dessous.">
                <OhSelect id="assistant-business_model" data-testid="assistant-business_model"
                          value={f.business_model} onChange={(e) => choisirMetier(e.target.value)}>
                  <option value="">Choisir un métier</option>
                  {listes.metiers.map((m) => <option key={m.id} value={m.id}>{m.libelle}</option>)}
                </OhSelect>
              </OhField>
              <OhField label="Parcours de création de dossier" htmlFor="assistant-dossier_creation_profile"
                       required className="mb-3">
                <OhSelect id="assistant-dossier_creation_profile" data-testid="assistant-dossier_creation_profile"
                          value={f.dossier_creation_profile}
                          onChange={(e) => maj("dossier_creation_profile", e.target.value)}>
                  <option value="">Choisir un parcours</option>
                  {listes.parcours.map((p) => <option key={p.id} value={p.id}>{p.libelle}</option>)}
                </OhSelect>
              </OhField>
              <OhField label="Activité des dossiers" htmlFor="assistant-dossier_activite" required className="mb-3">
                <OhSelect id="assistant-dossier_activite" data-testid="assistant-dossier_activite"
                          value={f.dossier_activite}
                          onChange={(e) => maj("dossier_activite", e.target.value)}>
                  <option value="">Choisir une activité</option>
                  {listes.activites.map((a) => <option key={a.id} value={a.id}>{a.libelle}</option>)}
                </OhSelect>
              </OhField>
            </>
          )}

          {etape === 1 && (
            <>
              {encartPrereglage}
              <p className="text-xs mb-3" style={{ color: "var(--oh-text-secondary)" }}>
                Un hub fermé masque toute sa section. Un module désactivé rend ses routes
                indisponibles — la garde est côté API, ⛔ pas seulement dans le menu.
              </p>
              {hubs.map((h) => (
                <div key={h.id} className="mb-3">
                  {puce(f.hubs_actifs.includes(h.id), h.name,
                        () => maj("hubs_actifs", bascule(f.hubs_actifs, h.id)),
                        `assistant-hub-${h.id}`)}
                  {f.hubs_actifs.includes(h.id) && (
                    <div className="flex flex-wrap gap-2 mt-2 ml-4">
                      {(h.modules || []).map((m) => {
                        const actif = m.required || f.modules.some(([a, b]) => a === h.id && b === m.id);
                        return puce(actif, m.name + (m.required ? " (requis)" : ""),
                          () => { if (m.required) return;
                            const p = f.modules.some(([a, b]) => a === h.id && b === m.id);
                            maj("modules", p ? f.modules.filter(([a, b]) => !(a === h.id && b === m.id))
                                             : [...f.modules, [h.id, m.id]]); },
                          `assistant-module-${h.id}-${m.id}`);
                      })}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {etape === 2 && (
            <>
              {encartPrereglage}
              <div className="rounded-lg p-3 mb-3 text-sm"
                   style={{ background: "var(--oh-info-bg)", color: "var(--oh-info-text)",
                            border: "0.5px solid var(--oh-info-border)" }}>
                <strong>{f.capabilities.length} activées</strong> · <strong>{refusees} refusées</strong>.
                ⛔ Une capacité non cochée est écrite à <code>False</code>, ⛔ pas laissée absente :
                une clé absente serait interprétée comme autorisée.
              </div>
              {Object.entries(parCategorie).map(([hub, liste]) => (
                <div key={hub} className="mb-3">
                  <div className="text-xs mb-1" style={{ color: "var(--oh-text-muted)" }}>{hub}</div>
                  <div className="flex flex-wrap gap-2">
                    {liste.map((c) => puce(f.capabilities.includes(c.id), c.name || c.id,
                      () => maj("capabilities", bascule(f.capabilities, c.id)),
                      `assistant-cap-${c.id}`))}
                  </div>
                </div>
              ))}
              <div className="mt-4 rounded-lg p-3 text-sm"
                   style={{ background: "var(--oh-bg-surface)", color: "var(--oh-text-secondary)",
                            border: "0.5px solid var(--oh-border)" }}>
                <div className="mb-1" style={{ color: "var(--oh-text)" }}>
                  Primitives Platform — fournies, ⛔ non gouvernables
                </div>
                {PRIMITIVES_PLATFORM.map((p) => (
                  <div key={p.id} className="text-xs">• <strong>{p.nom}</strong> — {p.motif}</div>
                ))}
              </div>
            </>
          )}

          {etape === 3 && (
            <>
              {champ("Nom de l'utilisateur initial", "admin_nom", { placeholder: "Optionnel" })}
              {champ("Adresse e-mail", "admin_email", { placeholder: "admin@exemple.fr" })}
              <div className="rounded-lg p-3 text-sm"
                   style={{ background: "var(--oh-info-bg)", color: "var(--oh-info-text)",
                            border: "0.5px solid var(--oh-info-border)" }}>
                Cet utilisateur reçoit le rôle <strong>admin</strong> sur cet espace, et lui seul.
                Un mot de passe provisoire est généré ; il devra le changer à la première
                connexion. ⛔ Il n'est jamais affiché ni journalisé.
              </div>
            </>
          )}

          {etape === 4 && (
            <>
              <p className="text-xs mb-3" style={{ color: "var(--oh-text-secondary)" }}>
                Les compteurs sont clés par <strong>slug</strong>. Le préfixe est dérivé du slug.
              </p>
              <div className="flex flex-wrap gap-2">
                {SEQ_PROPOSEES.map((s) => puce(f.sequences.includes(s), s,
                  () => maj("sequences", bascule(f.sequences, s)), `assistant-seq-${s}`))}
              </div>
              <div className="mt-3 text-xs" style={{ color: "var(--oh-text-muted)" }}>
                Préfixes : {f.sequences.map((s) =>
                  `${(slugFinal.replace(/[^a-z0-9]/g, "").slice(0, 4) || "ten").toUpperCase()}-${s.slice(0, 3)}`
                ).join(" · ") || "—"}
              </div>
            </>
          )}

          {etape === 5 && (
            <div className="text-sm space-y-2" style={{ color: "var(--oh-text)" }}>
              <div><span style={{ color: "var(--oh-text-secondary)" }}>Organisation :</span> {f.nom}</div>
              <div><span style={{ color: "var(--oh-text-secondary)" }}>Slug :</span> <code>{slugFinal}</code></div>
              {f.societe && <div><span style={{ color: "var(--oh-text-secondary)" }}>Société :</span> {f.societe}</div>}
              <div><span style={{ color: "var(--oh-text-secondary)" }}>Métier :</span> {libelleDe0(listes.metiers, f.business_model)}</div>
              <div><span style={{ color: "var(--oh-text-secondary)" }}>Parcours de dossier :</span> {libelleDe0(listes.parcours, f.dossier_creation_profile)}</div>
              <div><span style={{ color: "var(--oh-text-secondary)" }}>Activité des dossiers :</span> {libelleDe0(listes.activites, f.dossier_activite)}</div>
              <div><span style={{ color: "var(--oh-text-secondary)" }}>Hubs :</span> {f.hubs_actifs.join(", ")}</div>
              <div><span style={{ color: "var(--oh-text-secondary)" }}>Modules :</span> {f.modules.map((m) => m.join(".")).join(", ")}</div>
              <div><span style={{ color: "var(--oh-text-secondary)" }}>Capacités :</span> {f.capabilities.length} activées, {refusees} refusées</div>
              <div><span style={{ color: "var(--oh-text-secondary)" }}>Utilisateur :</span> {f.admin_email}</div>
              <div><span style={{ color: "var(--oh-text-secondary)" }}>Séquences :</span> {f.sequences.join(", ")}</div>
              {erreur && (
                <div className="rounded-lg p-3 flex gap-2"
                     style={{ background: "var(--oh-error-bg)", color: "var(--oh-error-text)",
                              border: "0.5px solid var(--oh-error-border)" }}>
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{erreur}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pied */}
        <div className="flex items-center justify-between p-4"
             style={{ borderTop: "0.5px solid var(--oh-border)" }}>
          <button type="button" onClick={() => setEtape((e) => Math.max(0, e - 1))}
                  disabled={etape === 0} aria-label="Étape précédente"
                  className="px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"
                  style={{ border: "0.5px solid var(--oh-border)", color: "var(--oh-text-secondary)",
                           opacity: etape === 0 ? 0.4 : 1 }}>
            <ChevronLeft className="w-4 h-4" /> Précédent
          </button>
          {etape < ETAPES.length - 1 ? (
            <button type="button" onClick={() => setEtape((e) => e + 1)} disabled={!valide}
                    aria-label="Étape suivante" data-testid="assistant-suivant"
                    className="px-4 py-1.5 rounded-lg text-sm flex items-center gap-1"
                    style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)",
                             opacity: valide ? 1 : 0.4 }}>
              Suivant <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button type="button" onClick={creer} disabled={enCours}
                    aria-label="Créer l'espace de travail" data-testid="assistant-creer"
                    className="px-4 py-1.5 rounded-lg text-sm flex items-center gap-2"
                    style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)",
                             opacity: enCours ? 0.5 : 1 }}>
              {enCours ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Créer l'espace de travail
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
